import React from "react";
import { Pill, Popover, Typography, useI18nContext } from "@procore/core-react";
import { ResourceBreakdownJobTitlePill } from "./resource-breakdown-job-title-pill";
import type { ResourceBreakdownItem } from "../types";

interface ResourceBreakdownProps {
   assignmentBreakdown: ResourceBreakdownItem[];
   requestBreakdown: ResourceBreakdownItem[];
   assignmentCount: number;
   requestCount: number;
}

export function TopColumnResourceBreakdownDisplay({
   assignmentBreakdown,
   requestBreakdown,
   assignmentCount,
   requestCount,
}: ResourceBreakdownProps) {
   const I18n = useI18nContext();

   const assignmentPopoverContent = (
      <Popover.Content placement="top">
         <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "18px" }}>
            {assignmentBreakdown.map((breakdown, index) => (
               <ResourceBreakdownJobTitlePill
                  key={index}
                  count={breakdown.count}
                  name={breakdown.name}
                  color={breakdown.color}
               />
            ))}
         </div>
      </Popover.Content>
   );

   const requestPopoverContent = (
      <Popover.Content placement="top">
         <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "12px" }}>
            <Typography intent="small" weight="semibold">
               {requestCount} {I18n.t("views.company.workforce_planning.total_requests")}
            </Typography>
            {requestBreakdown.map((breakdown, index) => (
               <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <div
                     style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: breakdown.color || "#000000",
                     }}
                  />
                  <Typography intent="small" weight="semibold">
                     {breakdown.count}
                  </Typography>
                  <Typography intent="small" weight="semibold">
                     {breakdown.name}
                  </Typography>
               </div>
            ))}
         </div>
      </Popover.Content>
   );

   return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Popover trigger="click" placement="top" overlay={assignmentPopoverContent}>
            <Typography
               intent="small"
               style={{
                  color: "#0066cc",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  cursor: "pointer",
                  maxWidth: "100%",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
               }}
               className="column-toolbar__top-item"
               onClick={(e) => e.stopPropagation()}
            >
               {assignmentCount} {I18n.t("views.company.workforce_planning.assignments")}
            </Typography>
         </Popover>
         {requestCount > 0 && (
            <Popover trigger="click" placement="top" overlay={requestPopoverContent}>
               <Typography
                  intent="small"
                  style={{
                     color: "#0066cc",
                     textDecoration: "underline",
                     textUnderlineOffset: "2px",
                     cursor: "pointer",
                     maxWidth: "100%",
                     textOverflow: "ellipsis",
                     overflow: "hidden",
                     whiteSpace: "nowrap",
                  }}
                  className="column-toolbar__top-item"
                  onClick={(e) => e.stopPropagation()}
               >
                  ({requestCount})
               </Typography>
            </Popover>
         )}
      </div>
   );
}

export function BottomColumnResourceBreakdownDisplay({
   assignmentBreakdown,
   assignmentCount,
   requestCount,
}: ResourceBreakdownProps) {
   const I18n = useI18nContext();

   return (
      <div
         style={{
            background: "hsl(200, 8%, 94%)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            padding: "8px",
            overflowX: "auto",
            whiteSpace: "nowrap",
            gap: "8px",
         }}
      >
         <Typography intent="h3" style={{ flexShrink: 0, textTransform: "lowercase" }}>
            {assignmentCount} {I18n.t("views.company.workforce_planning.total_assignments")}
         </Typography>
         <Pill color="blue">
            {requestCount} {I18n.t("views.company.workforce_planning.requests")}
         </Pill>
         <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {assignmentBreakdown.map((breakdown, index) => (
               <ResourceBreakdownJobTitlePill
                  key={index}
                  count={breakdown.count}
                  name={breakdown.name}
                  color={breakdown.color}
               />
            ))}
         </div>
      </div>
   );
}
